import { StyleSheet } from '@react-pdf/renderer';

export const pdfColors = {
  textHeading: '#1A202C',
  textStrong: '#11181C',
  text: '#2D3748',
  textLight: '#4A5568',
  textLabel: '#687076',
  textMuted: '#718096',
  border: '#E2E8F0',
  borderMuted: '#CBD5E0',
  background: '#F7FAFC',
  backgroundMuted: '#EDF2F7',
  brand: '#563FD8',
  brandDark: '#4532AD',
  success: '#38A169',
  successBg: '#F0FFF4',
  error: '#E53E3E',
  errorBg: '#FFF5F5',
  link: '#4299E1',
  warning: '#ED8936',
  warningBg: '#FFFAF0',
  warningLabel: '#744210',
  info: '#3182CE',
  violet: '#805AD5',
  orange: '#DD6B20',
  textSubtle: '#A0AEC0',
  white: '#FFFFFF',
  videoIconBackground: 'rgba(255, 255, 255, 0.2)',
} as const;

export const sharedStyles = StyleSheet.create({
  // Common item container
  itemContainer: {
    marginBottom: 16,
    padding: 6,
  },

  // Common header styles
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },

  // Common title styles
  itemTitle: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
    flex: 1,
  },

  // Result badges (CORRECT/INCORRECT)
  resultBadgeCorrect: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: pdfColors.success,
  },

  resultBadgeIncorrect: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: pdfColors.error,
  },

  resultBadgeText: {
    fontSize: 8,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.white,
    letterSpacing: 0.3,
  },

  // Media placeholder styles
  mediaPlaceholder: {
    padding: 8,
    backgroundColor: pdfColors.backgroundMuted,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: pdfColors.borderMuted,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },

  mediaPlaceholderText: {
    fontSize: 9,
    color: pdfColors.textMuted,
    fontFamily: 'Inter-SemiBold',
  },

  // Content item container
  contentItem: {
    marginBottom: 6,
  },

  // Question/description text
  contentText: {
    fontSize: 10,
    color: pdfColors.textLight,
    lineHeight: 1.5,
  },

  // Answer section
  answerSection: {
    marginTop: 8,
    padding: 10,
    backgroundColor: pdfColors.white,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: pdfColors.border,
  },

  answerText: {
    fontSize: 10,
    color: pdfColors.text,
    lineHeight: 1.5,
  },

  // Feedback section
  feedbackSection: {
    marginTop: 8,
    padding: 10,
    backgroundColor: pdfColors.warningBg,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: pdfColors.warning,
  },
  feedbackLabel: {
    fontSize: 8,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.warningLabel,
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  feedbackText: {
    fontSize: 10,
    color: pdfColors.text,
    lineHeight: 1.5,
  },

  // Outcome badges (used by submission items and summary)
  badgeBase: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 9999,
  },
  badgeSatisfactory: {
    backgroundColor: pdfColors.success,
  },
  badgeNotYetSatisfactory: {
    backgroundColor: pdfColors.error,
  },
  badgePending: {
    backgroundColor: pdfColors.info,
  },
  badgeWithdrawn: {
    backgroundColor: pdfColors.warning,
  },
  badgeText: {
    fontSize: 9,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.white,
    letterSpacing: 0.5,
  },
});

export function getOutcomeBadgeStyle(
  outcome: string,
): (typeof sharedStyles)[keyof typeof sharedStyles] {
  const upper = outcome.toUpperCase();

  if (upper === 'SATISFACTORY') {
    return sharedStyles.badgeSatisfactory;
  }
  if (upper === 'NOT_YET_SATISFACTORY') {
    return sharedStyles.badgeNotYetSatisfactory;
  }
  if (upper === 'WITHDRAWN') {
    return sharedStyles.badgeWithdrawn;
  }

  return sharedStyles.badgePending;
}

// PDF is rendered client-side in an authenticated browser session, so
// window.location.origin resolves relative /files/... paths to same-origin
// URLs that the browser can fetch with existing credentials.
export function resolveRelativePath(path: string): string {
  if (path.startsWith('/') && typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function isAccessibleUrl(url: string): boolean {
  return url !== '' && !url.includes('localhost') && !url.includes('127.0.0.1');
}
